import {
  Box,
  Button,
  Flex,
  Grid,
  LoadingOverlay,
  Modal,
  MultiSelect,
  Select,
  Text
} from "@dehub/theme";
import { i18n, nameOptionsFilter, useForm } from "@dehub/theme";
import { yupResolver } from "mantine-form-yup-resolver";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import * as yup from "yup";
import { WarehouseApi, WarehouseStaffApi } from "@/apis";
import type { IWarehouseStaffRole, IWarehouseStaffScope } from "@/apis/warehouseStaff";
import type { IGeneral } from "@/models/General";
import HttpHandler from "@/utils/http-handler";
import { message } from "@/utils/message";
import { renderName } from "@/utils/render-name";
import { FlagSwitch, RoleCards } from "./StaffFormParts";
import { WAREHOUSE_STAFF_ROLES, WAREHOUSE_STAFF_SCOPES, getScopeLabel } from "../constants";

type IFormData = {
  userId: string;
  staffRole: string;
  scope: string;
  canManageStaff: boolean;
  isPrimary: boolean;
  warehouseIds: string[];
};

const schema = yup.object({
  userId      : yup.string().required(i18n.t("common:required")),
  staffRole   : yup.string().required(i18n.t("common:required")),
  scope       : yup.string().oneOf(["SINGLE", "MULTI"]).required(),
  canManageStaff: yup.boolean(),
  isPrimary   : yup.boolean(),
  warehouseIds: yup.array().of(yup.string().required()).min(1, i18n.t("staff:warehouse.minWarehouse")).required()
});

interface IWarehouseOption {
  id: string;
  refCode: string;
  name: string;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}

export function BulkAddStaffModal({ opened, onClose, onSuccess }: Props) {
  const { t } = useTranslation("staff");
  const partnerStaffs = useSelector((state: { general: IGeneral }) => state.general.partnerStaffs);
  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState<IWarehouseOption[]>([]);

  const staffOptions = useMemo(
    () =>
      (partnerStaffs as unknown as Array<{ id: string; firstName?: string; lastName?: string }> | undefined)?.map(
        (c) => ({
          label: renderName(c),
          value: c.id
        })
      ) ?? [],
    [partnerStaffs]
  );

  const warehouseOptions = useMemo(
    () => warehouses.map((w) => ({ label: `${w.refCode} · ${w.name}`, value: w.id })),
    [warehouses]
  );

  useEffect(() => {
    if (!opened) return;
    (async () => {
      try {
        const res = (await WarehouseApi.list({
          filter: { query: "" },
          offset: { page: 1, limit: 500 }
        })) as { rows: IWarehouseOption[] };
        setWarehouses(res?.rows ?? []);
      } catch (err) {
        message.error((err as HttpHandler)?.message ?? t("msg.warehouseListError"));
      }
    })();
  }, [opened, t]);

  const form = useForm<IFormData>({
    initialValues: {
      userId        : "",
      staffRole     : "",
      scope         : "MULTI",
      canManageStaff: false,
      isPrimary     : false,
      warehouseIds  : []
    },
    validate: yupResolver(schema)
  });

  useEffect(() => {
    if (opened) form.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const isKeeperRole = form.values.staffRole === "WAREHOUSE_KEEPER";

  useEffect(() => {
    if (!isKeeperRole) {
      if (form.values.canManageStaff) form.setFieldValue("canManageStaff", false);
      if (form.values.isPrimary) form.setFieldValue("isPrimary", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKeeperRole]);

  const handleSubmit = async (values: IFormData) => {
    setLoading(true);
    try {
      await WarehouseStaffApi.bulkCreate({
        userId        : values.userId,
        staffRole     : values.staffRole as IWarehouseStaffRole,
        scope         : values.scope as IWarehouseStaffScope,
        canManageStaff: values.canManageStaff,
        isPrimary     : values.isPrimary,
        warehouseIds  : values.warehouseIds
      });
      message.success(t("msg.bulkAddSuccess", { count: values.warehouseIds.length }));
      await onSuccess();
    } catch (err) {
      message.error((err as HttpHandler)?.message ?? t("msg.genericError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} centered title={t("warehouse.bulkTitle")} size="lg">
      <Box pos="relative">
        <LoadingOverlay visible={loading} />
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label={t("warehouse.user")}
                placeholder={t("warehouse.userPlaceholder")}
                searchable
                filter={nameOptionsFilter}
                data={staffOptions}
                withAsterisk
                {...form.getInputProps("userId")}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label={t("warehouse.scope")}
                data={WAREHOUSE_STAFF_SCOPES.map((s) => ({ label: getScopeLabel(t, s.code), value: s.code }))}
                {...form.getInputProps("scope")}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <Text fz={14} fw={500} mb={6}>
                {t("warehouse.role")} <Text span c="red.8">*</Text>
              </Text>
              <RoleCards
                t={t}
                roles={WAREHOUSE_STAFF_ROLES.map((r) => r.code)}
                value={form.values.staffRole}
                onChange={(role) => form.setFieldValue("staffRole", role)}
                error={form.errors.staffRole as string | undefined}
              />
            </Grid.Col>
            {isKeeperRole ? (
              <Grid.Col span={12}>
                <Flex direction="column" gap="xs">
                  <FlagSwitch
                    label={t("warehouse.primary")}
                    description={t("warehouse.primaryDescription")}
                    checked={form.values.isPrimary}
                    onChange={(checked) => form.setFieldValue("isPrimary", checked)}
                  />
                  <FlagSwitch
                    label={t("warehouse.manageFlag")}
                    description={t("warehouse.manageFlagDescription")}
                    checked={form.values.canManageStaff}
                    onChange={(checked) => form.setFieldValue("canManageStaff", checked)}
                  />
                </Flex>
              </Grid.Col>
            ) : null}
            <Grid.Col span={12}>
              <MultiSelect
                label={t("warehouse.warehouses")}
                placeholder={t("warehouse.userPlaceholder")}
                data={warehouseOptions}
                searchable
                withAsterisk
                {...form.getInputProps("warehouseIds")}
              />
            </Grid.Col>
          </Grid>

          <Flex justify="flex-end" gap="sm" mt="md">
            <Button variant="default" onClick={onClose} type="button">
              {t("common:actions.cancel")}
            </Button>
            <Button type="submit" loading={loading}>
              {t("warehouse.addCount", { count: form.values.warehouseIds.length })}
            </Button>
          </Flex>
        </form>
      </Box>
    </Modal>
  );
}
